<template>
  <InfusePageLayout>
    <div class="flex flex-col gap-6 overflow-y-auto min-h-[30vh]">
      <MessageTable
        :has-more-items="hasNextPage"
        :page="currentPage"
        :loading="isLoading"
        :rows="messages"
        :date-range="filterOptions.range"
        :filters="filters"
        :filter-configs="filterConfigs"
        :search-enabled="false"
        @click-row="(row: ApiMessage) => redirectToDetails(router, row.id, 'messages.charging-station-message')"
        @load="fetchNextPage"
        @add:filter="(f) => onAddFilter(f)"
        @remove:filter="(f) => onRemoveFilter(f)"
        @update:date-range="onDateRangeChange"
      />
    </div>
  </InfusePageLayout>
</template>
<script setup lang="ts">
import { MessageTable } from '@infuse/e-connector/message/ui';
import { LogMessageDateRange, Message as ApiMessage, MessageType } from '@infuse/api/e-connector/message';
import { InfusePageLayout } from '@infuse/ui/layouts';
import { computed, onMounted, reactive } from 'vue';
import { useRouter } from 'vue-router';
import { redirectToDetails } from '@infuse/e-connector/shared/utils';
import { Filter, FilterConfiguration } from '@infuse/ui/domain';
import { FilterCollection } from '@infuse/cpms/charging-station/filters';
import { useI18n } from 'vue-i18n';
import { createMessageFilterConfigs } from './message-filter-configs';
import { useMessages } from '@infuse/e-connector/message/queries';
import { useChargingStations } from '@infuse/e-connector/charging-station/queries';
import { useCentralSystems } from '@infuse/e-connector/central-system/queries';
import { ECONNECTOR_MAX_PAGE_SIZE } from '@infuse/e-connector/shared/queries';

const router = useRouter();
const { t } = useI18n();

const { messages, isLoading, hasNextPage, currentPage, fetchNextPage, filterOptions } = useMessages();
const { chargingStations, fetchAllPages: fetchAllChargingStations } = useChargingStations(ECONNECTOR_MAX_PAGE_SIZE);
const { centralSystems, fetchAllPages: fetchAllCentralSystems } = useCentralSystems(ECONNECTOR_MAX_PAGE_SIZE);

// Filters UI state
const filterCollection = reactive(new FilterCollection());
const filters = computed<Filter[]>(() => filterCollection.filters);

const filterConfigs = computed<FilterConfiguration[]>(() =>
  createMessageFilterConfigs(
    t,
    chargingStations.value.map((cs) => cs.name),
    centralSystems.value.map((cs) => cs.name)
  )
);

onMounted(async () => {
  filterOptions.value.range = LogMessageDateRange.LAST_10_MINUTES;
  await fetchAllChargingStations();
  await fetchAllCentralSystems();
});

function applyFiltersToQuery() {
  const current = filters.value;
  filterOptions.value.types = current.filter((f) => f.config.name === 'types').map((f) => f.selectedOption.name as MessageType);
  filterOptions.value.versions = current.filter((f) => f.config.name === 'versions').map((f) => f.selectedOption.name as string);
  filterOptions.value.actions = current.filter((f) => f.config.name === 'actions').map((f) => f.selectedOption.name as string);
  filterOptions.value.chargePoints = current.filter((f) => f.config.name === 'chargePoints').map((f) => f.selectedOption.name as string);
  filterOptions.value.centralSystems = current
    .filter((f) => f.config.name === 'centralSystems')
    .map((f) => f.selectedOption.name as string);
}

async function onAddFilter(f: Filter) {
  filterCollection.add(f);
  applyFiltersToQuery();
}

async function onRemoveFilter(f: Filter) {
  filterCollection.remove(f);
  applyFiltersToQuery();
}

function onDateRangeChange(newDateRange: LogMessageDateRange) {
  filterOptions.value.range = newDateRange;
}
</script>
